import { Enums } from '@bode-canada/core';
import { ExternalIdGroupByOrigin } from '@/constants/listing';
import { formatDatetime } from '@/utils/date';
import { UserRole } from '@/constants/user';
import { transformCommunityLabel } from '@/components/utils/cities-communities';
import NumberFormat, { CurrencyCode } from '@/ui-kit/helpers/NumberFormatHelper';
import UserListing from './user-listing';

const { ListingOriginEnum } = Enums;

export default class Listing {
  constructor({ listing = {}, users }) {
    Object.assign(this, { location: [] }, listing);
    this.isBode = this.origin === ListingOriginEnum.BODE;
    this.#setRegion(listing);
    this.#setImages(listing);
    this.#setUsers(users, listing);
  }

  get isRAE() {
    return this.origin === ListingOriginEnum.REPLIERS_RAE;
  }

  get isPillar9() {
    return this.origin === ListingOriginEnum.REPLIERS
      && this.checkOriginByExternalId(ExternalIdGroupByOrigin.Pillar9);
  }

  get isRepliers() {
    return this.origin === ListingOriginEnum.REPLIERS
      && this.checkOriginByExternalId(ExternalIdGroupByOrigin.Repliers);
  }

  get updatedAtFormatted() {
    return this.updatedAt
      ? formatDatetime({ datetime: this.updatedAt, format: 'dddd, MMMM D, YYYY, h:mm:ss A' })
      : '';
  }

  get totalBeds() {
    return this.beds + (this.halfBeds || 0) / 2;
  }

  get totalBaths() {
    return this.fullBaths + (this.halfBaths || 0) / 2;
  }

  get formattedPrice() {
    return this.price
      ? NumberFormat.formatCurrency(this.price, CurrencyCode.CanadianDollars)
      : '';
  }

  get fullAddress() {
    const unit = this.unit ? `${this.unit} ` : '';

    return `${unit}${this.address}, ${this.city}, ${this.province}`;
  }

  get owners() {
    return this.users.filter((user) => user.role === UserRole.HomeOwner);
  }

  get additionalHolders() {
    return this.users.filter((user) => user.role === UserRole.AdditionalTitleHolder);
  }

  get hasActiveOwners() {
    return this.owners.some((owner) => !!owner.email);
  }

  checkOriginByExternalId(desiredGroup) {
    const externalId = this.mlsNumber || this.externalId;

    return externalId?.startsWith(desiredGroup);
  }

  getUserRole(userId) {
    return userId && this.users.find((user) => user.userId === userId)?.role;
  }

  getIsUserOwner(userId) {
    return this.getUserRole(userId) === UserRole.HomeOwner;
  }

  getIsUserAth(userId) {
    return this.getUserRole(userId) === UserRole.AdditionalTitleHolder;
  }

  getIsUserTitleHolder(userId) {
    return this.getIsUserOwner(userId) || this.getIsUserAth(userId);
  }

  #setUsers(users, {
    phoneNumber,
    hasConsentDisplayPhoneNumberOnListing: isPhoneVisible,
  }) {
    this.users = (users || []).map((user) => new UserListing(user, phoneNumber, isPhoneVisible));
  }

  #setImages({ images }) {
    this.images = images || [];
  }

  #setRegion({ region }) {
    this.region = region
      ? transformCommunityLabel(region)
      : null;
  }
}
